import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, Code2, Layers, ExternalLink, Lock, Globe } from "lucide-react";
import Layout from "@/components/Layout";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/c4840e87-a4c7-46bd-9d8d-eb19340bf291/images/4e0001b27c2060fe3cc1cfb1295432038d7d43f8bf880395bb34e3a4c2f964e1.png";

const PARAMS = [
  { name: "type", req: "ja", def: "text", desc: "text · url · email · wifi · vcard · phone · sms" },
  { name: "data", req: "—", def: "—", desc: "Generischer Inhalt für text/url" },
  { name: "url", req: "—", def: "—", desc: "Bei type=url" },
  { name: "email", req: "—", def: "—", desc: "Bei type=email / vcard" },
  { name: "subject / body", req: "—", def: "—", desc: "Bei type=email" },
  { name: "phone", req: "—", def: "—", desc: "Bei type=phone / sms / vcard" },
  { name: "message", req: "—", def: "—", desc: "Bei type=sms" },
  { name: "ssid / password / encryption / hidden", req: "—", def: "WPA / false", desc: "Bei type=wifi" },
  { name: "first_name / last_name / organization / title / address", req: "—", def: "—", desc: "Bei type=vcard" },
  { name: "fg", req: "—", def: "#000000", desc: "Vordergrundfarbe (Hex)" },
  { name: "bg", req: "—", def: "#FFFFFF", desc: "Hintergrundfarbe (Hex)" },
  { name: "format", req: "—", def: "png", desc: "png oder svg" },
  { name: "scale", req: "—", def: "10", desc: "Modul-Größe in Pixel (1–40)" },
  { name: "border", req: "—", def: "2", desc: "Modul-Rand (0–20)" },
  { name: "error", req: "—", def: "H", desc: "Fehlerkorrektur L/M/Q/H" },
  { name: "center_text", req: "—", def: "—", desc: "Optionales Branding-Text in der Mitte (max 8 Zeichen)" },
];

export default function ApiDocs() {
  const { user } = useAuth();
  const base = (typeof window !== "undefined" ? window.location.origin : "") + "/api/public/qr";
  const examples = [
    { id: "url", label: "URL", url: `${base}?type=url&data=https://makrolog.de&fg=%230A52FF&bg=%23FFFFFF` },
    { id: "wifi", label: "WLAN", url: `${base}?type=wifi&ssid=MyNet&password=secret&encryption=WPA` },
    { id: "vcard", label: "Kontakt", url: `${base}?type=vcard&first_name=Max&last_name=Mustermann&organization=Makrolog%20AG&phone=%2B49301234&email=max@makrolog.de` },
    { id: "svg", label: "SVG-Format", url: `${base}?type=text&data=Hallo%20Welt&format=svg` },
  ];

  return (
    <Layout hideNav={!user}>
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 md:py-12" data-testid="api-docs-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-bold">Public API</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-1">QR-Codes per HTTP GET</h1>
            <p className="mt-3 text-zinc-600 max-w-xl">
              Generiere QR-Codes ohne Authentifizierung — direkt in deinem Browser, im Backend oder via <span className="font-mono">curl</span>. Gibt PNG oder SVG zurück. Standardfarben: schwarz auf weiß.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-xs font-mono">
                <Globe className="w-3.5 h-3.5" /> GET /api/public/qr
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-zinc-200 text-xs">
                <Lock className="w-3.5 h-3.5" /> 100 Anfragen / Minute / IP
              </span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-zinc-200 rounded-md overflow-hidden">
              <img src={HERO_IMG} alt="" className="w-full h-48 object-cover" aria-hidden />
            </div>
          </div>
        </div>

        {/* Endpoint */}
        <section className="mt-12">
          <div className="overline text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">Endpoint</div>
          <div className="mt-3 border border-zinc-200 bg-white rounded-md overflow-hidden">
            <CodeBlock
              code={base + "?type=url&data=https://makrolog.de"}
              testId="endpoint-code"
            />
          </div>
        </section>

        {/* Parameters */}
        <section className="mt-10">
          <div className="overline text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">Parameter</div>
          <div className="mt-3 border border-zinc-200 bg-white rounded-md overflow-x-auto">
            <table className="w-full text-sm" data-testid="params-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                  <th className="px-4 py-3">Parameter</th>
                  <th className="px-4 py-3">Pflicht</th>
                  <th className="px-4 py-3">Default</th>
                  <th className="px-4 py-3">Beschreibung</th>
                </tr>
              </thead>
              <tbody>
                {PARAMS.map((p) => (
                  <tr key={p.name} className="border-b border-zinc-100 last:border-0">
                    <td className="px-4 py-2.5 font-mono text-zinc-900 align-top">{p.name}</td>
                    <td className="px-4 py-2.5 text-zinc-600 align-top">{p.req}</td>
                    <td className="px-4 py-2.5 font-mono text-zinc-600 align-top">{p.def}</td>
                    <td className="px-4 py-2.5 text-zinc-700 align-top">{p.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Examples */}
        <section className="mt-10">
          <div className="overline text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">Beispiele</div>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="examples-grid">
            {examples.map((ex) => (
              <ExampleCard key={ex.id} ex={ex} />
            ))}
          </div>
        </section>

        {/* Code samples */}
        <section className="mt-10">
          <div className="overline text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">Code</div>
          <Tabs defaultValue="curl" className="mt-3" data-testid="code-tabs">
            <TabsList className="bg-zinc-100">
              <TabsTrigger value="curl">curl</TabsTrigger>
              <TabsTrigger value="js">JavaScript</TabsTrigger>
              <TabsTrigger value="python">Python</TabsTrigger>
              <TabsTrigger value="html">HTML</TabsTrigger>
            </TabsList>
            <TabsContent value="curl">
              <CodeBlock
                code={`curl -L "${base}?type=url&data=https://makrolog.de&fg=%230A52FF" -o qr.png`}
              />
            </TabsContent>
            <TabsContent value="js">
              <CodeBlock
                code={`const res = await fetch("${base}?type=text&data=Hallo&format=svg");
const svg = await res.text();
document.querySelector("#out").innerHTML = svg;`}
              />
            </TabsContent>
            <TabsContent value="python">
              <CodeBlock
                code={`import requests
r = requests.get("${base}", params={
  "type": "url", "data": "https://makrolog.de", "fg": "#0A52FF"
})
open("qr.png", "wb").write(r.content)`}
              />
            </TabsContent>
            <TabsContent value="html">
              <CodeBlock
                code={`<img src="${base}?type=url&data=https://makrolog.de" alt="QR" width="240" height="240" />`}
              />
            </TabsContent>
          </Tabs>
        </section>

        {!user && (
          <div className="mt-12 border border-zinc-200 bg-white rounded-md p-6 flex items-start gap-4">
            <span className="grid place-items-center w-10 h-10 rounded-md bg-[#0A52FF] text-white shrink-0">
              <Layers className="w-5 h-5" />
            </span>
            <div className="flex-1">
              <div className="font-display text-lg font-semibold">Mehr Funktionen mit Login</div>
              <p className="text-sm text-zinc-600 mt-1">
                Mit Anmeldung über deine <span className="font-mono">@makrolog.de</span>-Adresse erhältst du Branding mit Logo,
                Verlauf und mehr Anpassungen.
              </p>
            </div>
            <Link to="/login" className="self-center">
              <Button className="bg-zinc-900 hover:bg-zinc-800 text-white">Anmelden</Button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}

function ExampleCard({ ex }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md overflow-hidden flex" data-testid={`example-${ex.id}`}>
      <div className="w-32 shrink-0 bg-zinc-50 grid place-items-center border-r border-zinc-200 p-3">
        <img src={ex.url} alt={ex.label} className="w-24 h-24 object-contain" loading="lazy" />
      </div>
      <div className="flex-1 p-4 min-w-0">
        <div className="text-xs uppercase tracking-wider text-zinc-500">{ex.label}</div>
        <div className="mt-1 font-mono text-[11px] break-all text-zinc-800 line-clamp-3">{ex.url}</div>
        <div className="mt-3 flex gap-2">
          <a href={ex.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
            <ExternalLink className="w-3.5 h-3.5" /> Öffnen
          </a>
          <button
            onClick={() => { navigator.clipboard.writeText(ex.url); toast.success("URL kopiert"); }}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50"
            data-testid={`copy-${ex.id}`}
          >
            <Copy className="w-3.5 h-3.5" /> Kopieren
          </button>
        </div>
      </div>
    </div>
  );
}

function CodeBlock({ code, testId }) {
  return (
    <div className="relative bg-[#0B0B0E] text-zinc-100 font-mono text-xs leading-relaxed">
      <button
        onClick={() => { navigator.clipboard.writeText(code); toast.success("Code kopiert"); }}
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 text-[11px]"
        data-testid={testId ? `${testId}-copy` : undefined}
      >
        <Copy className="w-3 h-3" /> Kopieren
      </button>
      <pre className="p-4 overflow-x-auto" data-testid={testId}><code>{code}</code></pre>
    </div>
  );
}
